/**
 * What each tab shows under the preview widget: other sizes (72), query results (76), label changes (77), the
 * color-vision strip (78), the widgets a click filters and the motion timeline (79).
 */
import type { ReactNode } from "react";
import { CIcon } from "../icons";
import { DATASETS, colorFor, displayName, formatNumber, type ChartSettings } from "../chartSettings";
import type { Shape, WidgetSize } from "../chartTypes";
import { contrast, simulate, type Vision } from "../paletteChecks";
import type { ChartTheme } from "../themes";
import type { DatasetResult } from "../../types";
import { FIELD_ICON } from "./DataTab";
import { paletteOf } from "./ColorsTab";
import { EASING_LABEL } from "./InteractTab";

export interface Change { id: string; icon: string; what: string; text: string; undo: () => void }

export function OtherSizes({ size, render }: { size: WidgetSize; render: (s: WidgetSize) => ReactNode }) {
  const others = (["L", "M", "S"] as WidgetSize[]).filter((z) => z !== size);
  return (
    <div className="cs-other">
      <p className="cs-other__k">Same chart at other sizes</p>
      <div className="cs-other__row">{others.map((z) => <div key={z}>{render(z)}</div>)}</div>
    </div>
  );
}

/** "Query results" (76): the rows the chart is built from, five at a time. */
export function QueryResults({ result, settings, onOpenFull }: { result: DatasetResult; settings: ChartSettings; onOpenFull: () => void }) {
  const ds = DATASETS[settings.data.datasetId];
  const m = settings.data.mapping;
  const field = (id?: string) => ds.fields.find((f) => f.id === id);
  const cat = field(m.category);
  // Every dataset field the rows carry besides the label and value (tooltip fields, ward, …).
  const extras = ds.fields.filter((f) => f.id !== m.category && f.id !== m.value.field && result.rows.some((r) => r[f.id] != null));
  const valueLabel = m.value.agg === "count" ? "count" : `${m.value.agg} of ${field(m.value.field)?.label ?? m.value.field}`;
  const unit = "unit" in result.shape && result.shape.unit ? result.shape.unit : "count";
  const rows = result.rows.slice(0, 5);
  return (
    <div className="cs-results">
      <p className="cs-results__k">Query results</p>
      <table className="cs-results__table">
        <thead><tr>
          <th><CIcon name={cat ? FIELD_ICON[cat.type] : "tag"} size={12} />{cat?.label ?? "name"}</th>
          {extras.map((f) => <th key={f.id}><CIcon name={FIELD_ICON[f.type]} size={12} />{f.label}</th>)}
          <th><CIcon name={unit === "usd" ? "dollar-sign" : "hash"} size={12} />{valueLabel}</th>
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={String(r.key)}><td>{String(r.label ?? r.key)}</td>
            {extras.map((f) => <td key={f.id}>{String(r[f.id] ?? "")}</td>)}
            <td>{formatNumber(Number(r.value ?? 0), { format: "full", decimals: 0 }, unit)}</td></tr>))}</tbody>
      </table>
      <div className="cs-results__foot">
        <span>Grouped by {cat?.label ?? "—"} · {valueLabel} · showing {rows.length} of {result.total}</span>
        <button type="button" className="link-button" onClick={onOpenFull}>Open full data <CIcon name="arrow-right" size={11} /></button>
      </div>
    </div>
  );
}

export function LabelChangesCard({ changes }: { changes: Change[] }) {
  return (
    <div className="cs-changes">
      <p className="cs-changes__k">{changes.length} label change{changes.length === 1 ? "" : "s"} on this chart</p>
      {changes.map((c) => (
        <div key={c.id} className="cs-changes__row"><CIcon name={c.icon} size={13} /><span className="cs-changes__what">{c.what}</span>
          <span className="cs-changes__text">{c.text}</span><button type="button" className="link-button" onClick={c.undo}>Undo</button></div>
      ))}
    </div>
  );
}

const VISIONS: { id: Vision | "gray"; label: string }[] = [{ id: "protan", label: "Protanopia" }, { id: "deutan", label: "Deuteranopia" }, { id: "tritan", label: "Tritanopia" }, { id: "gray", label: "Grayscale" }];

/** 78: the chart's real colors through each color-vision simulation, with how far apart the two main colors stay. */
export function VisionStrip({ settings, shape, theme }: { settings: ChartSettings; shape: Shape; theme?: ChartTheme }) {
  const palette = paletteOf(theme);
  const rows = shape.kind === "category" || shape.kind === "signed" ? shape.rows.slice(0, 5) : shape.kind === "parts" ? shape.parts.slice(0, 5) : [];
  if (!rows.length) return null;
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  const colors = rows.map((r) => colorFor(r.key, r.value, settings.colors, palette));
  const distinct = [...new Set(colors)];
  return (
    <div className="cs-vision" aria-label="Color-vision simulation">
      {VISIONS.map((v) => {
        const sim = colors.map((c) => simulate(c.slice(0, 7), v.id));
        let apart: number | null = null;
        if (distinct.length > 1) { apart = Infinity; for (let i = 0; i < distinct.length; i++) for (let j = i + 1; j < distinct.length; j++) apart = Math.min(apart, contrast(simulate(distinct[i].slice(0, 7), v.id), simulate(distinct[j].slice(0, 7), v.id))); }
        return (
          <div key={v.id} className="cs-vision__card"><span>{v.label}</span>
            <div className="cs-vision__bars">{rows.map((r, i) => <i key={r.key} style={{ width: `${(Math.abs(r.value) / max) * 100}%`, background: sim[i] }} />)}</div>
            {apart !== null && <small>{apart.toFixed(1)}:1 apart</small>}
          </div>
        );
      })}
    </div>
  );
}

const DESCRIBE: Record<string, (n: string) => string> = {
  alderVotes: (n) => `Showing Ald. ${n}’s recorded votes`,
  forecastWatch: (n) => `Toss-ups narrowed to Ald. ${n}`,
};

/** 79: the other widgets a click filters, ringed, with their filter chip. */
export function AlsoFiltering({ targets, sel }: { targets: { title: string; icon: string; type: string }[]; sel: { key: string; label: string } | null }) {
  const short = sel ? sel.label.replace(/\s+\(\d+\)$/, "").split(/\s+/).at(-1)! : null;
  return (
    <div className="cs-alsof">
      <p className="cs-alsof__k">Also filtering on this overview</p>
      <div className="cs-alsof__row">{targets.slice(0, 2).map((t) => (
        <div key={t.title} className={`cs-alsof__card${sel ? " is-ringed" : ""}`}>
          <div className="cs-alsof__head"><CIcon name={t.icon} size={13} /><strong>{t.title}</strong>{short && <span className="cs-chip-filter"><CIcon name="funnel" size={11} />{short}</span>}</div>
          {short && DESCRIBE[t.type] && <p>{DESCRIBE[t.type](short)}</p>}
        </div>))}</div>
    </div>
  );
}

const EASE: Record<ChartSettings["interact"]["easing"], (t: number) => number> = { cubicOut: (t) => 1 - (1 - t) ** 3, quarticOut: (t) => 1 - (1 - t) ** 4, linear: (t) => t };

/** 79: "When added · Grow · 300 ms · cubic-out" with four frames computed from the same easing, and Replay. */
export function MotionTimeline({ settings, shape, theme, onReplay }: { settings: ChartSettings; shape: Shape; theme?: ChartTheme; onReplay: () => void }) {
  const it = settings.interact;
  const palette = paletteOf(theme);
  const rows = shape.kind === "category" ? shape.rows.slice(0, 5) : [];
  const max = Math.max(...rows.map((r) => r.value), 1);
  const frames = [0, 1, 2, 3].map((i) => Math.round((it.duration * i) / 3));
  const verb = { grow: "Grow", fade: "Fade", none: "None" }[it.enter];
  return (
    <div className="cs-timeline">
      <div className="cs-timeline__head"><CIcon name="play" size={12} /><strong>When added · {verb} · {it.duration} ms · {EASING_LABEL[it.easing].split(" · ").at(-1)}</strong>
        <span className="spacer" /><button type="button" className="link-button" onClick={onReplay}>Replay</button></div>
      <div className="cs-timeline__frames">{frames.map((ms) => {
        const t = it.duration ? EASE[it.easing](ms / it.duration) : 1;
        return (
          <div key={ms} className="cs-timeline__frame">
            {rows.map((r) => <i key={r.key} style={{ width: `${(r.value / max) * (it.enter === "grow" ? t : 1) * 100}%`, opacity: it.enter === "fade" ? t : 1,
              background: colorFor(r.key, r.value, settings.colors, palette), height: it.enter === "grow" && t === 0 ? 0 : undefined }} />)}
            <small>{ms} ms</small>
          </div>
        );
      })}</div>
    </div>
  );
}

export const renameKeyOf = (shape: Shape, label: string) => (shape.kind === "category" || shape.kind === "signed" ? shape.rows : shape.kind === "parts" ? shape.parts : []).find((r) => r.label === label)?.key ?? label;
export { displayName };
